import React, { Component } from "react";
import { Marked } from "marked";
import { markedHighlight } from "marked-highlight";
import hljs from "highlight.js/lib/common";
import DOMPurify from "dompurify";

import { MERMAID_CONFIG } from "./mermaidTheme";
import "highlight.js/styles/github.css";
import "./markdown.css";

// Shared Marked instance with syntax highlighting. Used by every widget
// that renders user-authored markdown so the visual treatment is the
// same everywhere (thread widget, message widget, …).
const playgroundMarked = new Marked(
  markedHighlight({
    langPrefix: "hljs language-",
    highlight(code, lang) {
      // A mermaid fence is a DIAGRAM, not source: it must not be highlighted, and must not be
      // HTML-escaped either, since the definition is handed back to mermaid verbatim below.
      if ( "mermaid" === lang ) {
        return code;
      }
      const language = lang && hljs.getLanguage(lang) ? lang : "plaintext";
      try {
        return hljs.highlight(code, { language, ignoreIllegals: true }).value;
      } catch (e) {
        return code;
      }
    }
  })
);

// A ```mermaid fence becomes a placeholder that MarkdownBody fills in after mount. It cannot be
// rendered here: mermaid's API is asynchronous and measures text against a live element. The
// definition rides in a `data-` attribute (DOMPurify keeps those by default) rather than in the
// element's text, so the page never flashes a wall of arrows before the diagram lands.
playgroundMarked.use({
  renderer: {
    code(token) {
      if ( "mermaid" !== token.lang ) {
        return false; // fall through to the highlighter above
      }
      return `<div class="playground-mermaid" data-mermaid="${encodeURIComponent(token.text)}"></div>`;
    }
  }
});

// Mermaid is loaded on demand: it is a large dependency and most markdown carries no diagram, so
// the cost is paid by the first view that actually shows one.
let mermaidPromise = null;
function loadMermaid() {
  if ( !mermaidPromise ) {
    mermaidPromise = import("mermaid").then(({ default: mermaid }) => {
      // Palette, security and the list of keys a diagram may not override, all in one place —
      // see mermaidTheme.js for why each is set the way it is.
      mermaid.initialize(MERMAID_CONFIG);
      return mermaid;
    });
  }
  return mermaidPromise;
}

let mermaidSeq = 0;

/**
 * MarkdownBody — the one markdown renderer, now with mermaid.
 *
 * A diagram that fails to parse falls back to its own source in a `<pre>`, not to an empty box: a
 * broken diagram is usually one somebody needs to read and fix, and hiding the text hides the fix.
 *
 * Props:
 *   text      — the markdown source
 *   className — added to `.playground-markdown`
 *   breaks    — treat a single newline as a line break (see below)
 *
 * **`breaks` is for text somebody TYPED**, and it is off by default because the two kinds of
 * markdown here want opposite things. A document — an `.md` artifact, a plan, a report — is written
 * with hard-wrapped paragraphs, and honouring every newline in one shreds it into ragged lines. A
 * message typed into a field is the other way round: Enter sends and Shift+Enter is a newline, so
 * every newline in it was somebody deliberately starting a new line, and joining two of them into a
 * paragraph loses what they wrote. GitHub draws the same line between a comment box and a file.
 */
export class MarkdownBody extends Component {
  constructor(props) {
    super(props);
    this.ref = React.createRef();
    this.cancelled = false;
  }

  componentDidMount() {
    this.renderDiagrams();
  }

  componentDidUpdate(previous) {
    if ( previous.text !== this.props.text ) {
      this.renderDiagrams();
    }
  }

  componentWillUnmount() {
    this.cancelled = true;
  }

  async renderDiagrams() {
    const root = this.ref.current;
    if ( !root ) return;
    const nodes = Array.from(root.querySelectorAll(".playground-mermaid[data-mermaid]"));
    if ( 0 === nodes.length ) return;

    let mermaid;
    try {
      mermaid = await loadMermaid();
    } catch (e) {
      return; // unavailable — the placeholders stay empty rather than taking the page down
    }
    if ( this.cancelled ) return;

    for ( const node of nodes ) {
      const definition = decodeURIComponent(node.getAttribute("data-mermaid") || "");
      node.removeAttribute("data-mermaid");
      try {
        const { svg } = await mermaid.render(`bh-mermaid-${++mermaidSeq}`, definition);
        if ( this.cancelled ) return;
        node.innerHTML = svg;
      } catch (e) {
        if ( this.cancelled ) return;
        node.classList.add("playground-mermaid-failed");
        const pre = document.createElement("pre");
        pre.textContent = definition;
        node.innerHTML = "";
        node.appendChild(pre);
      }
    }
  }

  render() {
    const { text, className, breaks } = this.props;
    if ( typeof text !== "string" ) {
      return null;
    }
    const cls = className ? `playground-markdown ${className}` : "playground-markdown";
    // Per call, not per instance: the same renderer serves documents and typed messages, and the
    // option does not leak from one parse to the next.
    const html = { __html: DOMPurify.sanitize(playgroundMarked.parse(text, { breaks: !!breaks })) };
    return <div ref={this.ref} className={cls} dangerouslySetInnerHTML={html} />;
  }
}

export default MarkdownBody;
